import { PDFViewer } from "@react-pdf/renderer";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import PreviewIcon from "@assets/icons/previewIcon";
import ActionMenu from "@components/ActionMenu/ActionMenu";
import CustomizedButton from "@components/ButtonConfirm/ButtonConfirm";
import { useCreateInvoiceHook } from "@hooks/CreateInvoiceHook/useCreateInvoiceHook";
import { Stack } from "@mui/material";
import InvoicePreviewPDF from "../PreviewPdfModal/InvoicePreviewPDF";
import BankInfo from "./BankInfo/BankInfo";
import "./index.scss";
import InvoiceDetails from "./InvoiceDetails/InvoiceDetails";
import InvoiceTable from "./InvoiceTable/InvoiceTable";
import PersonalDetails from "./PersonalDetails/PersonalDetails";
import TotalPrices from "./TotalPrices/TotalPrices";
import { useGetCurrentBankQuery } from "store/bank/bank.api";
import { useCreateInvoiceDraftMutation } from "store/invoice/invoiceApi";

const CreateNewInvoice = ({ isUpdate, invoiceData, onSave }) => {
  console.log("invoiceData dans CreateNewInvoice:", invoiceData);
  const [createInvoiceDraft, { isLoading: isLoadingdraft }] =
    useCreateInvoiceDraftMutation();

  const { t } = useTranslation();
  const {
    articleLines,
    totals,
    extraInputs,
    companiesData,
    clientsData,
    selectedCompanyId,
    selectedClientId,
    note,
    dateEcheance,
    dateDeFacturation,
    paymentTerms,
    currency,
    isCreating,

    handleLineChange,
    handleAddLine,
    handleRemoveLine,
    handleCompanyChange,
    handleExtraInputChange,
    handleAddExtraInput,
    handleRemoveExtraInput,
    handleCreateInvoice,
    handleDragEnd,

    setClientName,
    setClientEmail,
    setClientAddress,
    setSellerName,
    sellerName,
    setSellerEmail,
    sellerEmail,
    clientEmail,
    clientName,
    clientsError,
    companiesError,
    currentUser,
    getInvoicePreviewData,
    isLoadingClients,
    isLoadingCompanies,
    paymentInfo,
    sellerAddress,
    setArticleLines,
    setTotals,
    clientAddress,
    setSellerAddress,
    setNote,
    setPaymentTerms,
    setDateEcheance,
    setDateDeFacturation,
    setSelectedClientId,
    setSelectedCompanyId,
    setEngagementId,

    showPreview,
    setShowPreview,
    isLoadingNextNumber,
    nextInvoiceData,
    engagementId,
    offersResponse,
    isLoadingOffers,
    offersError,
    dureeEcheance,
    setDureeEcheance,
    isLoadingBankData,
    invoiceStatus,
  } = useCreateInvoiceHook();
  console.log("🚀 ~ CreateNewInvoice ~ currentUser:", currentUser);
  console.log("articleLines", articleLines);
  const { data: currentBankData, isLoading: isLoadingBankData1 } =
    useGetCurrentBankQuery(currentUser?.companyId?._id);
  // Injection des données d'édition dans le state
  console.log("currentBankData?.data?._id", currentBankData?.data?._id);
  useEffect(() => {
    if (isUpdate && invoiceData) {
      setSelectedCompanyId(
        typeof invoiceData.companyId === "object"
          ? invoiceData.companyId?._id
          : invoiceData.companyId || ""
      );
      if (invoiceData.clientId) {
        setSelectedClientId(invoiceData.clientId);
      } else {
        const client = clientsData.find(
          (c) => c.name === invoiceData.clientName
        );
        setSelectedClientId(client?._id || "");
      }

      setClientName(invoiceData.clientName || "");
      setClientEmail(invoiceData.clientEmail || "");
      setClientAddress(invoiceData.clientAddress || "");
      setSellerName(invoiceData.sellerName || "");
      setSellerEmail(invoiceData.sellerEmail || "");
      setSellerAddress(invoiceData.sellerAddress || "");
      setNote(invoiceData.note || "");
      setDateDeFacturation(invoiceData.dateDeFacturation?.split("T")[0] || "");
      setDateEcheance(invoiceData.dateEcheance?.split("T")[0] || "");
      setEngagementId(invoiceData.engagementId || "");

      const lines = (invoiceData.offers || []).map((line) => ({
        name: line?.idOffer?.name || "",
        description: line?.description || "",
        quantity: line.quantity || 0,
        price: line.price || 0,
        TVA: line.TVA ?? 0,
        reductionPercent: Number(line.reductionPercent) || 0,
        reductionAmount: Number(line.reductionAmount) || 0,
        TotalHt: Number(((line.price || 0) * (line.quantity || 0)).toFixed(2)),
        idOffer: line.idOffer?._id || "",
      }));
      console.log("lines", lines);

      if (lines.length === 0) {
        // Si pas d'offres, on met une ligne vide par défaut pour afficher la table comme en création
        setArticleLines([
          {
            name: "",
            description: "",
            quantity: 0,
            price: 0,
            TVA: 0,
            reductionPercent: 0,
            reductionAmount: 0,
            TotalHt: 0,
            idOffer: "",
          },
        ]);
      } else {
        setArticleLines(lines);
      }
    }
  }, [isUpdate, invoiceData]);

  useEffect(() => {
    if (!articleLines || articleLines.length === 0) return;

    const subtotal = articleLines.reduce(
      (acc, line) => acc + Number(line.price || 0) * Number(line.quantity || 0),
      0
    );
    const discountAmount = articleLines.reduce(
      (acc, line) => acc + Number(line.reductionAmount || 0),
      0
    );
    const tva = articleLines.reduce(
      (acc, line) =>
        acc +
        ((Number(line.price || 0) * Number(line.quantity || 0) -
          Number(line.reductionAmount || 0)) *
          Number(line.TVA || 0)) /
          100,
      0
    );
    const ttc = subtotal - discountAmount + tva;

    setTotals({
      subtotal: Number(subtotal.toFixed(2)),
      discountAmount: Number(discountAmount.toFixed(2)),
      tva: Number(tva.toFixed(2)),
      ttc: Number(ttc.toFixed(2)),
    });
  }, [articleLines]);

  useEffect(() => {
    if (!dateDeFacturation) return;

    const factureDate = new Date(dateDeFacturation);
    factureDate.setDate(factureDate.getDate() + dureeEcheance);
    const newDueDate = factureDate.toISOString().split("T")[0];
    setDateEcheance(newDueDate);
  }, [dateDeFacturation, dureeEcheance, setDateEcheance]);
  // Gestion de la sauvegarde (création ou mise à jour)
  console.log("currentUser", currentUser?.companyId?._id);
  const handleSave = async () => {
    console.log(
      "totals.subtotal:",
      totals.subtotal,
      typeof totals.subtotal,
      isNaN(totals.subtotal)
    );
    const errors = [];

    if (!selectedCompanyId) {
      errors.push(t("invoice.error_company_required"));
    }

    if (!selectedClientId) {
      errors.push(t("invoice.error_client_required"));
    }

    if (!dateDeFacturation) {
      errors.push(t("invoice.error_date_facturation_required"));
    }

    if (!dateEcheance) {
      errors.push(t("invoice.error_date_echeance_required"));
    }

    if (!totals.subtotal || isNaN(Number(totals.subtotal))) {
      errors.push(t("invoice.invalid_ht"));
    }

    // 👉 S’il y a des erreurs, on les affiche toutes d’un coup
    if (errors.length > 0) {
      errors.forEach((err) => toast.error(err));
      return;
    }

    if (isUpdate) {
      const invoicePayload = {
        ...invoiceData,
        offers: articleLines.map((line) => ({
          idOffer: line.idOffer,
          quantity: Number(line.quantity),
          description: line.description,
          TVA: Number(line.TVA),
          reductionPercent: Number(line.reductionPercent) || 0,
          reductionAmount: Number(line.reductionAmount) || 0,
          TotalHt: Number(line.TotalHt ?? 0),
          TotalTTC: Number(line.TotalTTC),
          deposit: Number(line.deposit || 0),
          price: Number(line.price || 0),
        })),
        extraInputs,
        dureeEcheance,
        totalHT: Number(totals.subtotal),
        taxTVA: Number(totals.tva),
        total: Number(totals.ttc),
        companyId: currentUser?.companyId?._id,
        clientId: selectedClientId._id,
        dateDeFacturation: new Date(dateDeFacturation),
        dateEcheance: new Date(dateEcheance),
        note,
        sellerName,
        sellerEmail,
        sellerAddress,
        clientName,
        clientEmail,
        clientAddress,
        paymentTerms,
        currency,
        engagementId,
        paymentInfo,
        invoiceStatus: "A valider",
        idbank: currentBankData?.data?._id,
      };
      console.log(
        "invoicePayload.totalHT:",
        invoicePayload.totalHT,
        typeof invoicePayload.totalHT
      );
      console.log(
        "✅ totalHT typeof:",
        typeof invoicePayload.totalHT,
        invoicePayload.totalHT
      );

      onSave(invoicePayload);
    } else {
      handleCreateInvoice(); // création normale
    }
  };
  const handleSaveDraft = async () => {
    const draftStatus = "Brouillon";
    console.log("invoiceData", invoiceData);

    if (!articleLines || articleLines.length === 0) {
      toast.error("Ajoutez au moins une ligne d'offre.");
      return;
    }

    // Forcer le statut à "Brouillon" pour la validation

    const offersFiltered = articleLines
      .filter(
        (line) =>
          line.idOffer &&
          (typeof line.idOffer === "string" ||
            (line.idOffer._id && typeof line.idOffer._id === "string"))
      )
      .map((line) => ({
        idOffer:
          typeof line.idOffer === "string" ? line.idOffer : line.idOffer._id,
        quantity: Number(line.quantity),
        TVA: Number(line.TVA),
        reductionPercent: Number(line.reductionPercent) || 0,
        reductionAmount: Number(line.reductionAmount) || 0,
        TotalHt: Number(line.TotalHt ?? 0),
        TotalTTC: Number(line.TotalTTC),
        deposit: Number(line.deposit || 0),
      }));
    const invoicePayload = {
      ...invoiceData,
      offers: offersFiltered,
      extraInputs,
      dureeEcheance,
      totalHT: Number(totals.subtotal),
      taxTVA: Number(totals.tva),
      total: Number(totals.ttc),
      companyId: currentUser?.companyId?._id,
      clientId: selectedClientId,
      dateDeFacturation: new Date(dateDeFacturation),
      dateEcheance: new Date(dateEcheance),
      note,
      sellerName,
      sellerEmail,
      sellerAddress,
      clientName,
      clientEmail,
      clientAddress,
      paymentTerms,
      currency,
      engagementId,
      paymentInfo,
      invoiceStatus: draftStatus, // force draft
      typeDocument: "invoice", // or "quote", "credit_note"
      type: "sell", // or "buy"
      idbank: currentBankData?.data?._id,
    };
    try {
      const response = await createInvoiceDraft(invoicePayload);
      console.log("✅ Invoice draft created successfully:", response);
    } catch (err) {
      console.error("❌ Error creating invoice draft:", err);

      // Optional: show a toast error to the user
      toast.error(
        t("invoice.creation_failed") ||
          "Failed to create the invoice draft. Please try again."
      );
    }
  };

  return (
    <div className="invoice-container p-3">
      <ToastContainer />

      <div
        className="invoice-header mb-3"
        style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
        <div
          className="invoice-actions"
          style={{ display: "flex", gap: "10px" }}>
          <CustomizedButton
            finalizeText={t("invoice.save_draft")}
            bgColor={"#CACACA"}
            handleCreate={handleSaveDraft}
            stylee={{ minWidth: "200px" }}
          />

          <CustomizedButton
            finalizeText={t("invoice.preview")}
            handleCreate={() => setShowPreview(true)}
            bgColor={"#0F84FF"}
            icon={<PreviewIcon />}
            stylee={{ minWidth: "200px" }}
          />

          <CustomizedButton
            creatingText={t("invoice.creating")}
            finalizeText={t("invoice.finalize")}
            handleCreate={handleSave}
            isCreating={isCreating}
            stylee={{ minWidth: "200px" }}
          />
        </div>
      </div>

      <Stack className="invoice-section" gap={3}>
        <InvoiceDetails
          setDateDeFacturation={setDateDeFacturation}
          dateDeFacturation={dateDeFacturation}
          setDateEcheance={setDateEcheance}
          dateEcheance={dateEcheance}
          isLoadingNextNumber={isLoadingNextNumber}
          nextInvoiceData={nextInvoiceData}
          handleAddExtraInput={handleAddExtraInput}
          extraInputs={extraInputs}
          handleExtraInputChange={handleExtraInputChange}
          handleRemoveExtraInput={handleRemoveExtraInput}
          engagementId={engagementId}
          setEngagementId={setEngagementId}
          numeroInvoice={invoiceData?.numeroInvoice} // si update
          isUpdate={isUpdate}
          dureeEcheance={dureeEcheance}
          setDureeEcheance={setDureeEcheance}
        />
        <PersonalDetails
          sellerName={sellerName}
          sellerAddress={sellerAddress}
          sellerEmail={sellerEmail}
          selectedCompanyId={selectedCompanyId}
          handleCompanyChange={handleCompanyChange}
          isLoadingCompanies={isLoadingCompanies}
          companiesError={companiesError}
          companiesData={companiesData}
          selectedClientId={selectedClientId}
          setSelectedClientId={setSelectedClientId}
          clientsData={clientsData}
          setClientName={setClientName}
          setClientEmail={setClientEmail}
          setClientAddress={setClientAddress}
          isLoadingClients={isLoadingClients}
          clientsError={clientsError}
          clientName={clientName}
          clientAddress={clientAddress}
          currentUser={currentUser}
          idUserClient={selectedClientId}
          currentClientData={invoiceData?.clientId}
        />
      </Stack>

      <Stack
        className="invoice-section"
        sx={{
          backgroundColor: "#f8fbff",
          padding: "1.5rem",
          borderRadius: "16px",
        }}>
        <div className="invoice-table-wrapper d-flex ">
          <InvoiceTable
            articleLines={articleLines}
            offersResponse={offersResponse}
            isLoadingOffers={isLoadingOffers}
            offersError={offersError}
            handleLineChange={handleLineChange}
            handleRemoveLine={handleRemoveLine}
            handleDragEnd={handleDragEnd}
          />

          <ActionMenu addOffer={handleAddLine} deleteOffer={handleRemoveLine} />
        </div>
      </Stack>

      <TotalPrices totals={totals} />

      <BankInfo
        note={note}
        paymentInfo={paymentInfo}
        paymentTerms={paymentTerms}
        setNote={setNote}
        setPaymentTerms={setPaymentTerms}
        isLoadingBankData={isLoadingBankData}
      />

      <Modal
        isOpen={showPreview}
        onRequestClose={() => setShowPreview(false)}
        contentLabel="Prévisualisation de la facture"
        style={{ content: { width: "80vw", height: "90vh", margin: "auto" } }}>
        <button onClick={() => setShowPreview(false)} className="btn-modal">
          x
        </button>
        <PDFViewer width="100%" height="90%">
          <InvoicePreviewPDF
            invoiceData={getInvoicePreviewData()}
            paymentInfo={paymentInfo}
          />
        </PDFViewer>
      </Modal>
    </div>
  );
};

export default CreateNewInvoice;
